import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { AuthService } from './api/AuthAPI';

// Auth store. stratum-analytical uses jotai atoms (tokenAtom/userAtom) persisted
// to localStorage; here we use a React context to the same effect, keeping the
// token in localStorage so HttpRequest can attach it as a Bearer token.

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser);

  // The stored user is a snapshot taken at login, so a role changed since then
  // would not take effect until the next login — and localStorage is editable,
  // so the snapshot is not something to gate access on by itself. Re-read the
  // profile from the server on mount and trust that instead.
  //
  // A failed call leaves the stored user alone rather than logging out: the
  // request cannot tell a rejected token from a dropped connection, and the
  // server still enforces its own guards on every request either way.
  useEffect(() => {
    let cancelled = false;
    if (!localStorage.getItem('token')) return undefined;
    (async () => {
      const profile = await AuthService.getUserInfo();
      if (cancelled || !profile) return;
      setUser(prev => {
        const next = { ...prev, ...profile };
        try { localStorage.setItem('user', JSON.stringify(next)); } catch {}
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, []);

  const persist = useCallback((nextUser, token) => {
    if (token) localStorage.setItem('token', JSON.stringify(token));
    if (nextUser) localStorage.setItem('user', JSON.stringify(nextUser));
    setUser(nextUser || null);
  }, []);

  const login = useCallback(async (formData) => {
    const result = await AuthService.login(formData);
    if (!result || result.status === 'error') {
      return { status: 'error', message: result?.message || 'Login Failed' };
    }
    if (result.accessToken) {
      persist(result, result.accessToken);
      return { status: 'success', message: 'Login Successful', user: result };
    }
    return { status: 'error', message: 'Login Failed' };
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
